import { InputAdornment, Stack, TextFieldProps } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePickerProps as MUIDatePickerProps } from '@mui/x-date-pickers/DatePicker';
import { enUS as MUIenUS, nlNL } from '@mui/x-date-pickers/locales';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format, parseISO } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import nl from 'date-fns/locale/nl';
import { useCallback, useMemo } from 'react';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RefCallBack,
  RegisterOptions,
} from 'react-hook-form';
import { useBoolean } from 'usehooks-ts';

import { DATE_VALUE_FORMAT } from '@/constants/patterns';

import { useResponsive } from '@/hooks/useResponsive';
import { useTranslate } from '@/hooks/useTranslate';

import { elevationLevel } from '@/styles/constants/paper';

import { Language } from '@/types/i18n';

import { getDataTestId } from '@/utils/browser';

import { Icon } from '../Icon/Icon';

import { FieldArrow, FieldArrowButton, StyledDatePicker } from './Form.styled';

type DatePickerProps<FormPayload extends FieldValues> = {
  value?: Date;
  defaultValue?: Date;
  control: Control<FormPayload>;
  name: Path<FormPayload>;
  rules?: Omit<
    RegisterOptions<FormPayload, Path<FormPayload>>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;
  helperText?: TextFieldProps['helperText'];
  error?: TextFieldProps['error'];
} & Omit<MUIDatePickerProps<Date>, 'slotProps' | 'name' | 'value'>;

export function DatePicker<FormPayload extends FieldValues>({
  control,
  rules,
  name,
  error,
  helperText,
  ...props
}: DatePickerProps<FormPayload>) {
  const { value: open, setTrue: show, setFalse: hide } = useBoolean();
  const { isDesktop } = useResponsive();
  const { language } = useTranslate();

  const onInputClick = useCallback(() => {
    if (!isDesktop && !props.disabled) show();
  }, [isDesktop, props.disabled, show]);

  const localeByLangue = useMemo(() => {
    switch (language) {
      case Language.Dutch:
        return {
          adapter: nl,
          lang: nlNL.components.MuiLocalizationProvider.defaultProps.localeText,
        };
      default:
        return {
          adapter: enUS,
          lang: MUIenUS.components.MuiLocalizationProvider.defaultProps
            .localeText,
        };
    }
  }, [language]);

  const render = useCallback(
    ({
      value,
      onChange,
      onBlur,
      defaultValue,
      ref,
    }: {
      value: string;
      onChange: (value: string) => void;
      onBlur: TextFieldProps['onBlur'];
      defaultValue?: string;
      ref?: RefCallBack;
    }) => {
      const handleOnChange: DatePickerProps<Date>['onChange'] = (
        value: Date | null,
      ) => {
        onChange(value ? format(value, DATE_VALUE_FORMAT) : '');
      };

      return (
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={localeByLangue.adapter}
          localeText={localeByLangue.lang}
        >
          <StyledDatePicker
            ref={ref}
            value={value ? parseISO(value) : null}
            inputRef={ref}
            onChange={handleOnChange}
            defaultValue={defaultValue ? parseISO(defaultValue) : undefined}
            open={open}
            onClose={hide}
            reduceAnimations
            format="MM/dd/yyyy"
            slotProps={{
              dialog: {
                closeAfterTransition: false,
              },
              desktopPaper: {
                elevation: elevationLevel.l,
              },
              textField: {
                onBlur,
                onClick: onInputClick,
                helperText:
                  error && helperText ? (
                    open ? null : (
                      <Stack gap={0.5} component="span">
                        <Icon
                          name="alert"
                          size={20}
                          color={theme => theme.palette.error.main}
                        />
                        {helperText}
                      </Stack>
                    )
                  ) : (
                    helperText
                  ),
                error: !open && error,
                InputProps: {
                  ...getDataTestId(`${name || 'date'}-picker`),
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon name="calendar-blank" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <FieldArrowButton onClick={show} disabled={props.disabled}>
                      <FieldArrow name={open ? 'arrow-up' : 'arrow-down'} />
                    </FieldArrowButton>
                  ),
                },
              },
            }}
            {...props}
          />
        </LocalizationProvider>
      );
    },
    [
      error,
      helperText,
      hide,
      localeByLangue,
      name,
      onInputClick,
      open,
      props,
      show,
    ],
  );

  return (
    <Controller
      rules={rules}
      control={control}
      name={name}
      render={({
        field: { ref, onChange, value, onBlur },
        formState: { defaultValues },
      }) =>
        render({
          value,
          onChange,
          onBlur,
          defaultValue: defaultValues?.[name],
          ref,
        })
      }
    />
  );
}
